import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import { PasscodeForm } from "@/components/auth/PasscodeForm";
import { useAuth } from "@/hooks/useAuth";

export function ClientPasscodeEntry() {
  const { jwt } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsSetup, setNeedsSetup] = useState<boolean | null>(null);

  // Check if client needs passcode setup
  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch(`/api/client/${jwt}/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ passcode: "check" }),
        });
        const data = await res.json();
        if (data.needs_setup) {
          setNeedsSetup(true);
        } else {
          setNeedsSetup(false);
        }
      } catch {
        setNeedsSetup(false);
      }
    };
    check();
  }, [jwt]);

  const handleSetup = async (passcode: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/client/${jwt}/setup-passcode`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passcode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      setAuth({
        role: "client",
        jwt: jwt!,
        passcode,
        user: { _key: jwt!, name: "Partner", icon: "🧑", description: "" },
      });
      const redirect = searchParams.get("redirect");
      navigate(redirect || `/client/${jwt}/dashboard`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Setup failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (passcode: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/client/${jwt}/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passcode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      setAuth({
        role: "client",
        jwt: jwt!,
        passcode,
        user: data.client,
      });
      const redirectUrl = searchParams.get("redirect");
      navigate(redirectUrl || `/client/${jwt}/dashboard`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid passcode");
    } finally {
      setLoading(false);
    }
  };

  if (needsSetup === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app-bg">
        <div className="animate-pulse text-app-text-muted">Loading...</div>
      </div>
    );
  }

  return (
    <PasscodeForm
      mode={needsSetup ? "setup" : "enter"}
      onSubmit={needsSetup ? handleSetup : handleVerify}
      loading={loading}
      error={error}
    />
  );
}
